import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosClient from "../../api/api";
import Line from "../../components/InputComponents/Line";
import { PrimaryContext } from "../../components/MainContext/MainContext";
import {
  successToast,
  errorToast,
} from "../../adminPanel/components/toastAlerts/ToastAlerts";
import { BsTrash, BsStar, BsStarFill, BsPencil, BsCheck2 } from "react-icons/bs";
import georgianCities from "../../data/georgianCities";
import { apiError } from "../../api/apiError";

/**
 * User's home-delivery addresses.
 * Backend: /user-addresses (index, store, update, destroy) + /user-addresses/{id}/setDefault
 * The default address is the one home-delivery will ship to (once the switch happens).
 * `phone` is optional — if empty, the user's account phone is used at delivery time.
 */
const MyAddresses = () => {
  const { t } = useTranslation();
  const { user, setUpdateUser } = useContext(PrimaryContext);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ city: "", address: "", phone: "" });
  const [creating, setCreating] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axiosClient.get("/user-addresses");
      setAddresses(data);
    } catch (e) {
      errorToast(apiError(e, t("admin_failed_to_load") || "Failed to load addresses"));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchList();
  }, []);

  const resetDraft = () => setDraft({ city: "", address: "", phone: "" });

  const handleCreate = async () => {
    if (!draft.city || !draft.address) {
      errorToast(t("fill_all_fields") || "City and address are required");
      return;
    }
    try {
      await axiosClient.post("/user-addresses", {
        city: draft.city.trim(),
        address: draft.address.trim(),
        phone: draft.phone ? draft.phone.trim() : null,
      });
      successToast(t("successfully") || "Added");
      resetDraft();
      setCreating(false);
      fetchList();
      // Nudge MainContext to refetch /user so addresses_count updates and the popup dismisses.
      setUpdateUser && setUpdateUser(Date.now());
    } catch (err) {
      errorToast(apiError(err, t("something_went_wrong") || "Failed to add"));
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setDraft({ city: a.city, address: a.address, phone: a.phone || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetDraft();
  };

  const saveEdit = async () => {
    try {
      await axiosClient.put(`/user-addresses/${editingId}`, {
        city: draft.city.trim(),
        address: draft.address.trim(),
        phone: draft.phone ? draft.phone.trim() : null,
      });
      successToast(t("successfully") || "Saved");
      cancelEdit();
      fetchList();
    } catch (err) {
      errorToast(apiError(err, t("something_went_wrong") || "Failed to save"));
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        t("confirm_delete_address") || "Delete this address?"
      )
    )
      return;
    try {
      await axiosClient.delete(`/user-addresses/${id}`);
      successToast(t("successfully") || "Deleted");
      fetchList();
      setUpdateUser && setUpdateUser(Date.now());
    } catch (err) {
      errorToast(apiError(err, t("something_went_wrong") || "Failed to delete"));
    }
  };

  const setDefault = async (id) => {
    try {
      await axiosClient.post(`/user-addresses/${id}/setDefault`);
      successToast(t("default_updated") || "Default updated");
      fetchList();
    } catch (err) {
      errorToast(apiError(err, t("something_went_wrong") || "Failed to set default"));
    }
  };

  const displayPhone = (a) => a.phone || user?.phone || "—";
  const cityLabel = (value) => {
    const c = georgianCities.find((c) => c.value === value);
    return c ? c.label : value;
  };

  return (
    <div>
      <div className="flex flex-col gap-2 sm:w-full sm:justify-between md:items-center">
        <Line className="bg-teal_600 h-1 w-10" />
        <h2 className="font-semibold text-2xl md:text-xl">
          {t("my_addresses") || "ჩემი მისამართები"}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t("home_delivery_help") ||
            "მიუთითეთ ბინის მისამართები სადაც აპირებთ ამანათების მიღებას. სახლში მიტანის სერვისზე გადასვლისას მალე ვისარგებლებთ ამ მისამართებით."}
        </p>
      </div>

      {/* List */}
      <div className="mt-5 flex flex-col gap-2">
        {loading ? (
          <div className="animate-pulse bg-gray-200 w-full h-20 rounded-3xl" />
        ) : addresses.length === 0 ? (
          <div className="h-[120px] flex items-center justify-center bg-white rounded-3xl">
            <p className="font-bold text-gray-500">
              {t("no_addresses_yet") || "ჯერ არ დაგიმატებიათ მისამართი"}
            </p>
          </div>
        ) : (
          addresses.map((a) => (
            <div
              key={a.id}
              className={`w-full bg-white shadow-bs1 p-4 rounded-3xl md:rounded-lg ${
                a.is_default ? "border-2 border-teal_600" : ""
              }`}
            >
              {editingId === a.id ? (
                <div className="grid grid-cols-3 sm:grid-cols-1 gap-2 items-end">
                  <select
                    className="h-10 px-3 border border-slate-300 rounded bg-white"
                    value={draft.city}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, city: e.target.value }))
                    }
                  >
                    <option value="">{t("city") || "ქალაქი"}…</option>
                    {georgianCities.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    className="h-10 px-3 border border-slate-300 rounded"
                    placeholder={t("address") || "მისამართი"}
                    value={draft.address}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, address: e.target.value }))
                    }
                  />
                  <input
                    className="h-10 px-3 border border-slate-300 rounded"
                    placeholder={
                      t("phone_optional") ||
                      "ტელეფონი (თუ ცარიელია, ანგარიშის ტელეფონი გამოიყენება)"
                    }
                    value={draft.phone}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, phone: e.target.value }))
                    }
                  />
                  <div className="col-span-3 flex gap-2">
                    <button
                      onClick={saveEdit}
                      className="bg-teal_600 text-white rounded-full px-4 h-9 text-sm font-bold"
                    >
                      {t("save") || "შენახვა"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-200 rounded-full px-4 h-9 text-sm font-bold"
                    >
                      {t("cancel") || "გაუქმება"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex md:flex-col justify-between items-start gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-base">
                        {cityLabel(a.city)}, {a.address}
                      </p>
                      {a.is_default && (
                        <span className="text-xs bg-teal_600 text-white rounded-full px-2 py-0.5">
                          {t("default") || "მთავარი"}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {t("phone") || "ტელეფონი"}: {displayPhone(a)}
                      {!a.phone && (
                        <span className="text-gray-400">
                          {" "}
                          ({t("account_phone") || "ანგარიშის ტელეფონი"})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button
                      title={
                        a.is_default
                          ? t("already_default") || "მთავარი"
                          : t("set_as_default") || "მთავარად დანიშვნა"
                      }
                      onClick={() => !a.is_default && setDefault(a.id)}
                      disabled={a.is_default}
                      className={`p-2 rounded-full ${
                        a.is_default
                          ? "bg-teal_600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {a.is_default ? <BsStarFill /> : <BsStar />}
                    </button>
                    <button
                      title={t("edit") || "რედაქტირება"}
                      onClick={() => startEdit(a)}
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                      <BsPencil />
                    </button>
                    <button
                      title={t("delete") || "წაშლა"}
                      onClick={() => handleDelete(a.id)}
                      className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                    >
                      <BsTrash />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add new */}
      <div className="mt-5">
        {!creating ? (
          <button
            onClick={() => setCreating(true)}
            className="rounded-full text-white font-bold bg-teal_600 text-sm px-6 py-3"
          >
            + {t("add_new_address") || "ახალი მისამართის დამატება"}
          </button>
        ) : (
          <div className="bg-white shadow-bs1 p-4 rounded-3xl md:rounded-lg mt-3">
            <p className="font-semibold mb-3">
              {t("new_address") || "ახალი მისამართი"}
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-1 gap-2">
              <select
                className="h-10 px-3 border border-slate-300 rounded bg-white"
                value={draft.city}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, city: e.target.value }))
                }
              >
                <option value="">{t("city") || "ქალაქი"}…</option>
                {georgianCities.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                className="h-10 px-3 border border-slate-300 rounded"
                placeholder={t("address") || "მისამართი (ქუჩა, №, ბინა)"}
                value={draft.address}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, address: e.target.value }))
                }
              />
              <input
                className="h-10 px-3 border border-slate-300 rounded"
                placeholder={
                  t("phone_optional") ||
                  "ტელეფონი (არასავალდებულო)"
                }
                value={draft.phone}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, phone: e.target.value }))
                }
              />
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleCreate}
                className="rounded-full text-white font-bold bg-teal_600 text-sm px-6 py-2 flex items-center gap-1"
              >
                <BsCheck2 /> {t("save") || "შენახვა"}
              </button>
              <button
                onClick={() => {
                  setCreating(false);
                  resetDraft();
                }}
                className="rounded-full bg-gray-200 text-sm px-6 py-2 font-bold"
              >
                {t("cancel") || "გაუქმება"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAddresses;
